import {
  IRecyleBlukData,
  IRecyleIndividualData,
} from '@/typescript/interfaces/reports.interface';
import { environment } from 'environments/environment';

export const theadsIndividuals = [
  '',
  'Title',
  'Vin',
  'Vehicle',
  'Opening Price',
  'BuyNow Price',
  'Reserve Price',
  'Winner',
  'Highest Bid',
  'Approve Status',
  'Payment Status',
  'Recycle',
  'View',
];

export const individualColumns = [
  {
    title: '',
    data: 'imageUrl',
    render: (data: string) => {
      const src =
        data || environment.forntend_url + 'assets/img/user-placeholder.png';
      return `<div class="auction-dt-tableImage"><img src="${src}" style="height: 50px; width: 50px;" /></div>`;
    },
  },
  { title: 'Title', data: 'title' },
  { title: 'Vin', data: 'vin' },
  { title: 'Vehicle', data: 'vehicleName' },
  { title: 'Opening Price', data: 'opening_price' },
  { title: 'BuyNow Price', data: 'buy_now_price' },
  { title: 'Reserve Price', data: 'lowest_price' },
  { title: 'Winner', data: 'full_name' },
  { title: 'Highest Bid', data: 'highest_bid' },
  {
    title: 'Approve Status',
    data: 'action',
    render: (data: {
      admin: boolean;
      approval: boolean | null;
      auctionId: string;
    }) => {
      const { admin, approval, auctionId } = data;

      if (!admin) {
        return `<button type="button" class="btn curved yellow smallBtn px-4 mr-1" disabled>
          Not Required
        </button>`;
      }

      const approveDisabled = approval === true ? 'disabled' : '';
      const approveLabel = approval === true ? 'Approved' : 'Approve';

      const rejectDisabled = approval === false ? 'disabled' : '';
      const rejectLabel = approval === false ? 'Rejected' : 'Reject';

      const approveBtn =
        approval === true || approval === null
          ? `<button type="button" class="btn curved green smallBtn px-4 mr-1"
               ${approveDisabled}
               data-action="approve-individual"
               data-auction-id="${auctionId || ''}">
               ${approveLabel}
             </button>`
          : '';

      const rejectBtn =
        approval === false || approval === null
          ? `<button type="button" class="btn curved red smallBtn px-4"
               ${rejectDisabled}
               data-action="reject-individual"
               data-auction-id="${auctionId || ''}">
               ${rejectLabel}
             </button>`
          : '';

      return `<div class="auction-dt-tableTool">${approveBtn}${rejectBtn}</div>`;
    },
  },
  {
    title: 'Payment Status',
    data: 'payment',
    render: (data: boolean | null) => {
      if (data === null) {
        return `<div class="d-flex align-items-center justify-content-center">
          <img class="paymentIcons" src="${environment.forntend_url}assets/img/pending-clock-icon.png" />
          <p class="ml-2">Pending</p>
        </div>`;
      }
      if (data === true) {
        return `<div class="d-flex align-items-center justify-content-center">
          <img class="paymentIcons" src="${environment.forntend_url}assets/img/checkmark-flat.svg" />
          <p class="ml-2">Approved</p>
        </div>`;
      }
      // data === false
      return `<div class="d-flex align-items-center justify-content-center">
        <img class="paymentIcons" src="${environment.forntend_url}assets/img/red-x-icon.png" />
        <p class="ml-2">Rejected</p>
      </div>`;
    },
  },
  {
    title: 'Recycle',
    data: 'recycle',
    render: (data: { auctionId: string; approval: boolean | null }) => {
      const { auctionId, approval } = data;

      if (approval === false) {
        return `<button type="button"
            class="btn curved green smallBtn px-4 mr-1"
            data-action="individual-recycle"
            data-auction-id="${auctionId}">
          Recycle
        </button>`;
      }

      return `<button type="button"
          class="btn curved green smallBtn px-4 mr-1"
          disabled>
        Recycle
      </button>`;
    },
  },
  {
    title: 'View',
    data: 'view',
    render: (data: { auctionId: string }) => {
      return `<button class="auction-bidders" data-href="${data.auctionId}">
        <ion-icon name="eye" class="ion-lg color-yellow"></ion-icon>
      </button>`;
    },
  },
];

/**
 * Map API response items to a flat row shape used by the individual winners table
 * The component will still run `vehicleName()` to compute `vehicleName` using
 * `SharedService.InventoryName` so we keep `description_data` available.
 */
export function prepareIndividualTableData(auctions: IRecyleIndividualData[]) {
  if (!Array.isArray(auctions)) return [];

  return auctions.map((auction) => ({
    // image used in the table
    imageUrl: auction['vehicle_images']
      ? environment.imgBaseUrl +
        auction['vehicle_images']?.['parts_images'].filter(
          (part) => part.part_name === 'Front'
        )?.[0]?.['image']
      : environment.forntend_url + 'assets/img/car-placeholder.png',
    // basic fields used directly in template
    title: auction?.title || '--',
    vin: auction?.description_data?.['VIN Number'],
    vehicleName: auction?.vehicleName || '--',
    // prices
    opening_price: auction?.opening_price,
    buy_now_price: auction?.buy_now_price,
    lowest_price: auction?.lowest_price,

    full_name:
      auction?.winUserDetails?.first_name || auction?.winUserDetails?.last_name
        ? `${auction?.winUserDetails?.first_name} ${auction?.winUserDetails?.last_name}`
        : '--',
    // winner info
    highest_bid: auction?.winning_bid_amount,
    // flags and meta
    action: {
      admin: auction?.admin,
      approval: auction?.approval,
      auctionId: auction?.auction_id,
    },
    payment: auction?.payment,
    recycle: {
      auctionId: auction?.auction_id,
      approval: auction?.approval,
    },
    view: {
      auctionId: auction?.auction_id,
    },
  }));
}

export const theadsBulk = [
  '',
  'Title',
  'Total Vehicle',
  'Winner',
  'Reserve Bid',
  'Highest Bid',
  'Approve Status',
  'Payment Status',
  'Recycle',
  'View',
];

export const bulkColumns = [
  {
    title: '',
    data: 'imageUrl',
    render: (data: string) => {
      const src =
        data || environment.forntend_url + 'assets/img/user-placeholder.png';
      return `<div class="auction-dt-tableImage"><img src="${src}" style="height: 50px; width: 50px;" /></div>`;
    },
  },
  { title: 'Title', data: 'title' },
  { title: 'Total Vehicle', data: 'numberOfVehicle' },
  { title: 'Winner', data: 'full_name' },
  { title: 'Reserve Bid', data: 'lowest_price' },
  { title: 'Highest Bid', data: 'highest_bid' },
  {
    title: 'Approve Status',
    data: 'action',
    render: (data: {
      admin: boolean;
      approval: boolean | null;
      auctionId: string;
    }) => {
      const { admin, approval, auctionId } = data;

      if (!admin) {
        return `<button type="button" class="btn curved yellow smallBtn px-4 mr-1" disabled>
          Not Require
        </button>`;
      }

      const approveDisabled = approval === true ? 'disabled' : '';
      const approveLabel = approval === true ? 'Approved' : 'Approve';

      const rejectDisabled = approval === false ? 'disabled' : '';
      const rejectLabel = approval === false ? 'Rejected' : 'Reject';

      const approveBtn =
        approval === true || approval === null
          ? `<button type="button" class="btn curved green smallBtn px-4 mr-1"
               ${approveDisabled}
               data-action="approve-bluk"
               data-auction-id="${auctionId}">
               ${approveLabel}
             </button>`
          : '';

      const rejectBtn =
        approval === false || approval === null
          ? `<button type="button" class="btn curved red smallBtn px-4"
               ${rejectDisabled}
               data-action="reject-bluk"
               data-auction-id="${auctionId}">
               ${rejectLabel}
             </button>`
          : '';

      return `<div class="auction-dt-tableTool">${approveBtn}${rejectBtn}</div>`;
    },
  },
  {
    title: 'Payment Status',
    data: 'payment',
    render: (data: boolean | null) => {
      if (data === null) {
        return `<div class="d-flex align-items-center justify-content-center">
          <img class="paymentIcons" src="${environment.forntend_url}assets/img/pending-clock-icon.png" />
          <p class="ml-2">Pending</p>
        </div>`;
      }
      if (data === true) {
        return `<div class="d-flex align-items-center justify-content-center">
          <img class="paymentIcons" src="${environment.forntend_url}assets/img/checkmark-flat.svg" />
          <p class="ml-2">Approved</p>
        </div>`;
      }
      // data === false
      return `<div class="d-flex align-items-center justify-content-center">
        <img class="paymentIcons" src="${environment.forntend_url}assets/img/red-x-icon.png" />
        <p class="ml-2">Rejected</p>
      </div>`;
    },
  },
  {
    title: 'Recycle',
    data: 'recycle',
    render: (data: { auctionId: string; approval: boolean | null }) => {
      const { auctionId, approval } = data;

      if (approval === false) {
        return `<button type="button"
            class="btn curved green smallBtn px-4 mr-1"
            data-action="recycle-bulk"
            data-auction-id="${auctionId}">
          Recycle
        </button>`;
      }

      return `<button type="button"
          class="btn curved green smallBtn px-4 mr-1"
          disabled>
        Recycle
      </button>`;
    },
  },
  {
    title: 'View',
    data: 'view',
    render: (data: { auctionId: string }) => {
      return `<button class="auction-bidders" data-href="${data.auctionId}">
        <ion-icon name="eye" class="ion-lg color-yellow"></ion-icon>
      </button>`;
    },
  },
];

/**
 * Map API response items to a flat row shape used by the bulk winners table.
 */
export function prepareBulkTableData(auctions: IRecyleBlukData[]) {
  if (!Array.isArray(auctions)) return [];

  return auctions.map((auction) => ({
    // image
    imageUrl:
      auction?.['bulkInfo']?.[0]?.['BatchImageUrl']?.[0]?.['presigned_url'],
    // basic fields
    title: auction?.title || '--',
    numberOfVehicle: auction?.bulkInfo[0]?.total_vehicles ?? '--',
    // winner full name
    full_name:
      auction?.winUserDetails?.first_name || auction?.winUserDetails?.last_name
        ? `${auction?.winUserDetails?.first_name} ${auction?.winUserDetails?.last_name}`
        : '--',
    // prices
    lowest_price: auction?.lowest_price,
    highest_bid: auction?.winning_bid_amount,
    // approve status
    action: {
      admin: auction?.admin,
      approval: auction?.approval,
      auctionId: auction?.auction_id,
    },
    // payment status
    payment: auction?.payment,
    // recycle
    recycle: {
      auctionId: auction?.auction_id,
      approval: auction?.approval,
    },
    // view
    view: {
      auctionId: auction?.auction_id,
    },
  }));
}
